import React from "react"
import { Paragraph } from "grommet"
import commonStyles from "../pages/common-styles.module.scss"
import { getLinkByPageKey, pageKeys } from "../utils/navHelper"

const paraMargin = {
  top: "small",
  right: "small",
  bottom: "small",
  left: "small"
}

const faqData = {
  0: {
    question: "How do I know if I need therapy?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          Therapy can be useful to anyone who wants to improve the way they
          relate to themselves, others, and the world. This could take a
          multitude of forms. For example, It may be that you are missing out on
          opportunities due to fear of failure, you feel stuck in a
          relationship, job, or friendship group, or that whatever you do never
          seems to quieten the cruel self-critical voice in your head. There is
          no need to feel like you meet the criteria for any clinically
          recognised condition such as PTSD, Major Depression or Anxiety.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          Some people come to therapy with clear goals, some come with a general
          sense that their life could be better in some way. Many find that
          their goals evolve as therapy progresses: they become more specific,
          more realistic or ambitious, and new goals appear as people develop
          new insights into what is possible.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          If any of this is resonating with you, I suggest you{" "}
          {getLinkByPageKey(pageKeys.BOOK_A_SESSION, false, "book a session")}{" "}
          to talk about what brings you to seek therapy and how we could work
          together.
        </Paragraph>
      </>
    )
  },
  1: {
    question:
      "What difference is there, if any, between counselling, coaching, and therapy?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          The simple answer is that between <strong>counselling</strong> and{" "}
          <strong>therapy</strong> there is{" "}
          <strong>no important difference</strong>. I call what I do counselling
          and therapy, as do most counsellors. <strong>Coaching</strong>, on the
          other hand, <strong>is different</strong>, so I will try to explain
          here the relationship between these three words.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          <em>Counselling vs Therapy</em>
          <br />
          The wider picture is that counselling is a type of (psycho)therapy. On
          the NHS website, counselling is defined as “talking therapy that
          involves a trained therapist listening to you and helping you find
          ways to deal with emotional issues”. As this definition highlights,
          there is generally a strong focus on talking. Other forms of mental
          health treatment like dance or art therapy, which involve less or no
          talking, are unlikely to be considered counselling, but they are
          obviously therapy. I googled the root of the word therapy and
          apparently it comes from the Greek for 'healing' or 'curing'.
          <br />
          <br />
          Also, confusingly, therapy seems to have become synonymous with
          psychotherapy. When someone affirms uncharitably to another “you need
          therapy!” we know they are probably not referring to chemotherapy or
          massage therapy!
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          <em>Counselling/Therapy vs Coaching</em>
          <br />
          Whereas therapy is a healing process that invites a deep exploration
          of our emotional experience, coaching tends to keep to more specific,
          pragmatic goals. In my coaching work, I offer specific content to
          accompany and inform our work related to fatherhood and paternity,
          which gives a structure and direction that would be unusual in
          therapy.
          <br />
          <br />
          In coaching, we identify our strengths and the obstacles that get in
          the way of what we want, but there is usually less focus on uncovering
          the deeper psychological origins of those obstacles. Coaching can
          still involve emotion, but the emphasis is more on developing clarity,
          skills, and practical steps than on healing past wounds.
          <br />
          <br />
          As this difference suggests, therapy is often of a longer duration
          and/or the sessions are more frequent. It is more common, for example,
          for coaching sessions to be monthly, whereas therapy is more often
          weekly or fortnightly. Whilst the reasons for accessing therapy can be
          incredibly broad, it is the more appropriate option when someone is
          struggling with significant emotional distress or ongoing mental
          health concerns; whereas coaching is usually best when the focus is on
          growth, preparation, or navigating a specific transition.
        </Paragraph>
      </>
    )
  },
  2: {
    question:
      "How often would sessions with you be? And how long would it last?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          <em>Therapy (individual or couples)</em>
          <br />
          For individual and couples therapy I would normally suggest weekly
          sessions to begin with. This is beneficial to build rapport and
          momentum. With couples in particular, emotional intensity is often
          higher at the beginning and relationship patterns are more entrenched.
          In both individual and couples work, large gaps between sessions at
          the beginning can make the work feel disjointed and it is harder to
          build safety. Once the core issues have stabilised, fortnightly
          sessions are common and often very effective, and many people opt for
          maintenance sessions monthly or even less frequently.
          <br />
          <br />
          The length of time people see therapists varies wildly. Some people
          appear to get what they need from a few sessions, whilst most benefit
          from having more time. Whilst there is a lot of variation, couples
          generally have fewer sessions overall than individuals. However, in
          either format, it depends on many things. For example: the severity of
          your difficulties, what your expectations are, and your commitment to
          working on relevant challenges between sessions.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          <em>Coaching</em>
          <br />
          Coaching work tends to be more structured and time-limited than
          therapy. Clients with a specific goal of reduced scope can benefit
          from a single session! In our introductory call we can discuss the
          scope and timeline of our work and arrange a plan. This plan, of
          course, can be modified to fit the evolving needs of the client.
          <br />
          <br />
          For a more detailed description of the differences between coaching
          and therapy, see the question directly above about the difference
          between counselling, coaching, and therapy.
        </Paragraph>
      </>
    )
  },
  3: {
    question:
      "Why might someone need coaching for fatherhood? Isn't becoming a parent something people have always done naturally?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          Of course — becoming a father is something humans have done for as
          long as we've existed as a species, and until recently no one needed
          “coaching” for it. But the reality is that being a father today is
          nothing like it was for fathers generations ago, or even for our own
          Dads! We've never had the kinds of expectations and conditions we now
          face.
        </Paragraph>

        <Paragraph fill margin={paraMargin}>
          For example, in the UK, the inadequate length of paternity leave
          reflects the more traditional role of fathers: to provide financially.
          However, as understanding about child development and gender
          inequality have evolved, so have expectations about what being a
          father entails. We now understand far more about a father's impact on
          children's development — including attachment style. It used to be
          assumed attachment was something that formed only with the mother. We
          also know more about the importance of the role of Dads in whether or
          not Mothers develop post-partum depression. It has also become more
          normalised for men to actively want to be more involved and be more
          than a 'breadwinner'.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          Despite these changes in expectation and motivation, many of us lack
          the role models and tools to face this challenge. Many men haven't
          been socialised to care for a baby or young child, and that can make
          it hard to step into this role fully. Coaching offers a chance to slow
          down and look at ourselves and understand the barriers we've picked up
          along the way. This can help us become more involved and conscious
          fathers, enjoy the experience more, and at the same time maintain a
          healthy relationship with our partner, our work, and ourselves.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          Modern fatherhood carries a lot of responsibilities — coaching helps
          you meet them with more clarity, confidence, and support.
        </Paragraph>
      </>
    )
  },
  4: {
    question: "What would happen in the first session?",
    answer: (
      <Paragraph fill margin={paraMargin}>
        Regardless of whether it is individual therapy, coaching, or couples
        therapy, the first session is all about you feeling safe, comfortable,
        and listened to, and me feeling like I have an understanding of what
        brings you here, how you are now, and us agreeing where we want to go in
        our work together. The first session has more structure built into it
        that most other sessions. For example, in the first session we will
        cover some background information and go over things like
        confidentiality and our working agreement. In subsequent sessions the
        time is more tailored to exactly what you want to bring to that
        particular session.
      </Paragraph>
    )
  },
  5: {
    question: "What exactly is the process for becoming a client of yours?",
    answer: (
      <Paragraph fill margin={paraMargin}>
        On the {getLinkByPageKey(pageKeys.BOOK_A_SESSION, false)} page you can
        write to me either via email or the contact form expressing an interest
        in working together and we can arrange a free confidential introductory
        conversation. I use this to find out what you are looking for in therapy
        or coaching and address any concerns you have. If we agree to work
        together we will arrange a first full session to be conducted over Zoom
        or another suitable online platform.
      </Paragraph>
    )
  },
  6: {
    question:
      "What if I don't feel like I connect with you or that in some way the therapy is not working for me?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          The most important factor for therapy to meet a client's goals is the
          quality of the relationship between therapist and client.
          <sup>
            <a
              className={commonStyles.citationSuperscript}
              href="#ardito-raballino-2011"
            >
              1
            </a>
          </sup>{" "}
          It has also been found that the client, rather than the therapist, is
          a better judge of that quality.
          <sup>
            <a
              className={commonStyles.citationSuperscript}
              href="#bachelor-1991"
            >
              2
            </a>
          </sup>{" "}
          It is therefore essential you listen to your instincts and, if you are
          comfortable doing so, talk them over with people you trust or even the
          therapist if that feels OK. Ultimately, if it doesn't feel like it is
          working, then it probably isn't.
        </Paragraph>
        <Paragraph fill margin={paraMargin}>
          However, we must be careful not to mistake effort or some discomfort
          as signs of things not working. Therapy is not easy, but it should
          never feel punishing, humiliating or like you cannot express yourself.
        </Paragraph>
      </>
    )
  },
  7: {
    question:
      "How do you work with the inevitable power imbalance that exists between therapist and client?",
    answer: (
      <Paragraph fill margin={paraMargin}>
        Firstly by acknowledging that it inevitably exists, as it is a
        relationship between a paid professional and someone seeking help. It is
        my job to hold this power wisely and with accountability. I also aim to
        alleviate the power imbalance by encouraging clients to disagree with me
        when they want to, and by consistently checking in that they think we
        are following their goals, making it clear they are ultimately guiding
        the process.
      </Paragraph>
    )
  },
  8: {
    question: "What is it like having therapy online instead of in person?",
    answer: (
      <>
        <Paragraph fill margin={paraMargin}>
          It has its pros and cons. It removes the time and cost of the commute
          to/from therapy and allows people to have therapy in a familiar
          environment. Some people really appreciate doing therapy from the
          comfort of their own home; others would welcome a change of scene.
          Connectivity issues online are very rare depending on your connection
          speed, but some people miss the physical face-to-face contact. In my
          experience, working remotely is not a significant barrier to forming a
          strong therapeutic bond and the same tools and structure of sessions
          can be used, both when working with individuals or couples.
        </Paragraph>
      </>
    )
  }
}

export default faqData
